import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { cloneDeep, uniq } from 'lodash';
import { useMemo } from 'react';
import { useSessionContext } from 'contexts/session';

export const getProjectIds = (
  vnmProjectsData?: UserVnmProjectsQuery
): number[] =>
  uniq(
    vnmProjectsData?.user.accounts
      .map(a => a.utilityAccounts)
      .flat()
      .map(ua => ua.vnmSubscription?.vnmSolarProject?.id)
  ).filter(Boolean) as number[];

export const dedupeAddendums = (
  agreements: UserContractAgreementsQuery['userContractAgreements']
): UserContractAgreementsQuery['userContractAgreements'] =>
  agreements.map(agreement => {
    if ((agreement.contractVersion.addendums?.length || 0) < 2)
      return agreement;

    const clonedAgreement = cloneDeep(agreement);
    clonedAgreement.contractVersion.addendums = uniq(
      agreement.contractVersion.addendums
    );

    return clonedAgreement;
  });

interface Params {
  excludeAddendums?: boolean;
}

export const useContractAgreements = ({ excludeAddendums }: Params = {}) => {
  const { userId } = useSessionContext();

  const {
    data: vnmProjectsData,
    loading: projectsLoading,
    error: projectsError,
  } = useQuery<UserVnmProjectsQuery, UserVnmProjectsQueryVariables>(
    GET_VNM_PROJECTS,
    {
      skip: excludeAddendums,
      variables: { userId },
    }
  );

  const skipContractAgreements =
    (projectsLoading || !!projectsError || !vnmProjectsData) &&
    !excludeAddendums;
  const vnmSolarProjectIds = getProjectIds(vnmProjectsData);

  const { data, loading, error, refetch } = useQuery<
    UserContractAgreementsQuery,
    UserContractAgreementsQueryVariables
  >(GET_CONTRACT_AGREEMENTS, {
    skip: skipContractAgreements,
    variables: {
      userId,
      vnmSolarProjectIds,
      includeAddendums: !!vnmSolarProjectIds?.length,
    },
  });

  const agreements = useMemo(() => {
    if (!data) return undefined;
    return dedupeAddendums(data.userContractAgreements);
  }, [data]);

  return {
    data: agreements,
    error: error || projectsError,
    loading: loading || projectsLoading,
    refetch,
  };
};

export const GET_CONTRACT_AGREEMENTS = gql`
  query userContractAgreements(
    $userId: Int!
    $vnmSolarProjectIds: [Int!]!
    $includeAddendums: Boolean!
  ) {
    userContractAgreements(userId: $userId) {
      id
      createdAt
      fileUrl
      contractVersion {
        id
        contractId
        displayName
        contractType
        version
        internalName
        fileUrl
        addendums(vnmSolarProjectIds: $vnmSolarProjectIds)
          @include(if: $includeAddendums) {
          id
          fileUrl
        }
      }
      contractVersionLanguage {
        fileUrl
        id
      }
      executedContracts {
        fileUrl
        id
        vnmSolarProject {
          id
          name
        }
      }
    }
  }
`;

export const GET_VNM_PROJECTS = gql`
  query userVnmProjects($userId: Int!) {
    user(id: $userId) {
      id
      accounts {
        id
        utilityAccounts {
          id
          vnmSubscription {
            id
            vnmSolarProject {
              id
            }
          }
        }
      }
    }
  }
`;

export const getContractAgreementsMock: UserContractAgreementsQuery = {
  userContractAgreements: [
    {
      contractVersion: {
        contractType: 'terms',
        contractId: 1,
        displayName: 'General Terms',
        fileUrl: 'https://mock.com',
        id: 2,
        internalName: 'general_terms',
        version: 1.0,
      },
      contractVersionLanguage: {
        fileUrl: 'https://mock.com',
        id: 20,
      },
      createdAt: '2021-01-01',
      executedContracts: [],
      fileUrl: 'https://signed-mock.com',
      id: 1,
    },
    {
      contractVersion: {
        contractType: 'terms',
        contractId: 1,
        displayName: 'General Terms',
        fileUrl: 'https://mock.com',
        id: 2,
        internalName: 'general_terms',
        version: 1.3,
      },
      contractVersionLanguage: null,
      createdAt: '2021-01-01',
      executedContracts: [],
      fileUrl: 'https://signed-mock.com',
      id: 2,
    },
    {
      contractVersion: {
        contractType: 'privacy',
        contractId: 1,
        displayName: 'Privacy Policy',
        fileUrl: 'https://mock.com',
        id: 2,
        internalName: 'privacy_policy',
        version: 1.0,
      },
      contractVersionLanguage: null,
      createdAt: '2021-01-01',
      executedContracts: [],
      fileUrl: 'https://signed-mock.com',
      id: 3,
    },
    {
      contractVersion: {
        contractType: 'disclosure',
        contractId: 1,
        displayName: 'Community Solar Contract Disclosure Form',
        fileUrl: 'http://disclosure.contract.pdf',
        id: 1002,
        internalName: 'arcadia_standard_disclosure',
        version: 1.0,
      },
      contractVersionLanguage: null,
      createdAt: '2021-01-01',
      executedContracts: [],
      fileUrl: 'https://signed-mock.com',
      id: 102,
    },
  ],
};

export const getContractAgreementsWithAddendumsMock: UserContractAgreementsQuery =
  {
    userContractAgreements: [
      {
        contractVersion: {
          contractId: 3,
          addendums: [
            {
              fileUrl: 'https://countersignature-addendum.com',
              id: 12,
            },
          ],
          contractType: 'disclosure',
          displayName: 'Community Solar Contract Disclosure Form',
          fileUrl: 'http://disclosure.contract.pdf',
          id: 1002,
          internalName: 'arcadia_standard_disclosure',
          version: 1.0,
        },
        contractVersionLanguage: null,
        createdAt: '2021-01-01',
        executedContracts: [],
        fileUrl: 'https://signed-mock.com',
        id: 102,
      },
    ],
  };

export const getContractAgreementsWithExecutedMock: UserContractAgreementsQuery =
  {
    userContractAgreements: [
      {
        contractVersion: {
          contractId: 3,
          addendums: [
            {
              fileUrl: 'https://countersignature-addendum.com',
              id: 12,
            },
          ],
          contractType: 'disclosure',
          displayName: 'Community Solar Contract Disclosure Form',
          fileUrl: 'http://disclosure.contract.pdf',
          id: 1002,
          internalName: 'arcadia_standard_disclosure',
          version: 1.0,
        },
        contractVersionLanguage: null,
        createdAt: '2021-01-01',
        executedContracts: [
          {
            fileUrl: 'https://executed-mock.com',
            id: 1,
            vnmSolarProject: {
              id: 1,
              name: 'Mock Project',
            },
          },
        ],
        fileUrl: 'https://signed-mock.com',
        id: 102,
      },
    ],
  };

export const useContractAgreementsMock = {
  data: getContractAgreementsMock.userContractAgreements,
  error: undefined,
  loading: false,
};

export const userWithVnmProjectsMock: UserVnmProjectsQuery = {
  user: {
    id: 1,
    accounts: [
      {
        id: 1,
        utilityAccounts: [
          {
            id: 1,
            vnmSubscription: {
              id: 1,
              vnmSolarProject: {
                id: 1,
              },
            },
          },
        ],
      },
    ],
  },
};

export const userWithoutVnmProjectsMock: UserVnmProjectsQuery = {
  user: {
    id: 1,
    accounts: [
      {
        id: 1,
        utilityAccounts: [
          {
            id: 1,
            vnmSubscription: null,
          },
        ],
      },
    ],
  },
};
